import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useApproveOrgItem,
  useListOrgOrderItems,
  useRejectOrgItem,
  useUpdateOrgItem,
} from '../../features/org-orders/api';
import { assignCarrierDirect, removeCarrierDirect } from '../../features/carriers/api';
import { useCurrentUser } from '../../features/auth/api';
import { describeOrderActionError } from '../../features/org-orders/actionErrors';

import { useSortableColumns } from '../sorting/useSortableColumns';
import { useModal } from '../useModal';
import { useRowSelection } from '../useRowSelection';
import { useDebouncedSearch } from '../useDebouncedSearch';

// Filter rules — pure, unit-tested, no React.
// See features/org-orders/ordersFilters.
import {
  URL_KEYS,
  applyArrayParam,
  applyDateRange,
  applyStringParam,
  buildListParams,
  computeTotalPages,
  flattenOrderRows,
  getArrayParam,
  SALES_REP_SEED_KEY,
  resolveSalesRepSeed,
} from '../../features/org-orders/ordersFilters';

export function useOrgOrders() {
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Keep a mutable copy of the latest URL params.
   *
   * This prevents consecutive calls such as:
   *
   * setStatus(...)
   * setProductId(...)
   * setCarrierId(...)
   *
   * from overwriting one another before React Router has rendered
   * the preceding URL update.
   */
  const searchParamsRef = useRef(new URLSearchParams(searchParams.toString()));

  const searchParamsKey = searchParams.toString();

  /*
   * Local filter state.
   *
   * Values are initialized from the URL so a directly opened or
   * refreshed URL immediately restores the filters.
   */
  // BMP_QBO_INVOICE_FILTER_V1: preserve filter in the shareable URL.
  const [qboInvoice, setQboInvoiceState] = useState<'invoiced' | 'not_invoiced' | ''>(() => {
    const value = searchParams.get('qboInvoice');
    return value === 'invoiced' || value === 'not_invoiced' ? value : '';
  });

  const [search, setSearchState] = useState(() => searchParams.get(URL_KEYS.search) ?? '');

  const [status, setStatusState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.status)
  );

  const [productId, setProductIdState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.productId)
  );

  const [locationId, setLocationIdState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.locationId)
  );

  const [deliveryState, setDeliveryStateState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.deliveryState)
  );

  const [pickupState, setPickupStateState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.pickupState)
  );

  const [pickupCity, setPickupCityState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.pickupCity)
  );

  const [truckNumber, setTruckNumberState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.truckNumber)
  );

  const [driverName, setDriverNameState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.driverName)
  );

  const [carrierId, setCarrierIdState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.carrierId)
  );

  const [loadOption, setLoadOptionState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.loadOption)
  );

  const [salesRep, setSalesRepState] = useState<string[]>(() =>
    getArrayParam(searchParams, URL_KEYS.salesRep)
  );

  /* Read by the one-time seed effect, which must not re-run when it changes. */
  const salesRepRef = useRef(salesRep);
  salesRepRef.current = salesRep;

  const [tag, setTagState] = useState<string[]>(() => getArrayParam(searchParams, URL_KEYS.tag));

  const [orderDateFrom, setOrderDateFromState] = useState(
    () => searchParams.get(URL_KEYS.orderDateFrom) ?? ''
  );

  const [orderDateTo, setOrderDateToState] = useState(
    () => searchParams.get(URL_KEYS.orderDateTo) ?? ''
  );

  const [pickupDateFrom, setPickupDateFromState] = useState(
    () => searchParams.get(URL_KEYS.pickupDateFrom) ?? ''
  );

  const [pickupDateTo, setPickupDateToState] = useState(
    () => searchParams.get(URL_KEYS.pickupDateTo) ?? ''
  );

  const [deliveryDateFrom, setDeliveryDateFromState] = useState(
    () => searchParams.get(URL_KEYS.deliveryDateFrom) ?? ''
  );

  const [deliveryDateTo, setDeliveryDateToState] = useState(
    () => searchParams.get(URL_KEYS.deliveryDateTo) ?? ''
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const { data: me } = useCurrentUser();

  /*
   * Synchronize local state when the URL changes externally:
   *
   * - browser Back/Forward
   * - clicking a link to /orders?...filters
   * - pasting a filtered URL
   */
  useEffect(() => {
    const nextParams = new URLSearchParams(searchParamsKey);

    searchParamsRef.current = nextParams;
    setQboInvoiceState(
      (() => {
        const value = nextParams.get('qboInvoice');
        return value === 'invoiced' || value === 'not_invoiced' ? value : '';
      })()
    );
    setSearchState(nextParams.get('query') ?? '');
    setStatusState(getArrayParam(nextParams, 'status'));
    setProductIdState(getArrayParam(nextParams, 'product'));
    setLocationIdState(getArrayParam(nextParams, 'warehouse'));
    setDeliveryStateState(getArrayParam(nextParams, 'deliveryState'));
    setPickupStateState(getArrayParam(nextParams, 'pickupState'));
    setPickupCityState(getArrayParam(nextParams, 'pickupCity'));
    setTruckNumberState(getArrayParam(nextParams, 'truckNumber'));
    setDriverNameState(getArrayParam(nextParams, 'driver'));
    setCarrierIdState(getArrayParam(nextParams, 'carrier'));
    setLoadOptionState(getArrayParam(nextParams, 'loadOption'));
    setSalesRepState(getArrayParam(nextParams, 'salesRep'));

    setOrderDateFromState(nextParams.get('orderDateFrom') ?? '');
    setOrderDateToState(nextParams.get('orderDateTo') ?? '');

    setPickupDateFromState(nextParams.get('pickupDateFrom') ?? '');
    setPickupDateToState(nextParams.get('pickupDateTo') ?? '');

    setDeliveryDateFromState(nextParams.get('deliveryDateFrom') ?? '');
    setDeliveryDateToState(nextParams.get('deliveryDateTo') ?? '');
    setTagState(getArrayParam(nextParams, URL_KEYS.tag));
  }, [searchParamsKey]);

  /*
   * URL update helpers.
   */
  const updateUrlParams = useCallback(
    (update: (params: URLSearchParams) => void) => {
      const nextParams = new URLSearchParams(searchParamsRef.current.toString());

      update(nextParams);

      /*
       * Update the ref immediately so another setter called in the
       * same tick starts from these latest values.
       */
      searchParamsRef.current = nextParams;

      setSearchParams(nextParams, {
        replace: true,
      });
    },
    [setSearchParams]
  );

  const updateStringParam = useCallback(
    (key: string, value: string) => {
      updateUrlParams((params) => applyStringParam(params, key, value));
    },
    [updateUrlParams]
  );

  const updateArrayParam = useCallback(
    (key: string, values: string[]) => {
      updateUrlParams((params) => applyArrayParam(params, key, values));
    },
    [updateUrlParams]
  );

  /*
   * Filter setters update local state immediately and then mirror
   * the new value into the URL.
   */
  const setQboInvoice = useCallback(
    (value: 'invoiced' | 'not_invoiced' | '') => {
      setQboInvoiceState(value);
      updateStringParam('qboInvoice', value);
    },
    [updateStringParam]
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      updateStringParam(URL_KEYS.search, value);
    },
    [updateStringParam]
  );

  const setStatus = useCallback(
    (values: string[]) => {
      setStatusState(values);
      updateArrayParam(URL_KEYS.status, values);
    },
    [updateArrayParam]
  );

  const setProductId = useCallback(
    (values: string[]) => {
      setProductIdState(values);
      updateArrayParam(URL_KEYS.productId, values);
    },
    [updateArrayParam]
  );

  const setLocationId = useCallback(
    (values: string[]) => {
      setLocationIdState(values);
      updateArrayParam(URL_KEYS.locationId, values);
    },
    [updateArrayParam]
  );

  const setDeliveryState = useCallback(
    (values: string[]) => {
      setDeliveryStateState(values);
      updateArrayParam(URL_KEYS.deliveryState, values);
    },
    [updateArrayParam]
  );

  const setPickupState = useCallback(
    (values: string[]) => {
      setPickupStateState(values);
      updateArrayParam(URL_KEYS.pickupState, values);
    },
    [updateArrayParam]
  );

  const setPickupCity = useCallback(
    (values: string[]) => {
      setPickupCityState(values);
      updateArrayParam(URL_KEYS.pickupCity, values);
    },
    [updateArrayParam]
  );

  const setTruckNumber = useCallback(
    (values: string[]) => {
      setTruckNumberState(values);
      updateArrayParam(URL_KEYS.truckNumber, values);
    },
    [updateArrayParam]
  );

  const setDriverName = useCallback(
    (values: string[]) => {
      setDriverNameState(values);
      updateArrayParam(URL_KEYS.driverName, values);
    },
    [updateArrayParam]
  );

  const setCarrierId = useCallback(
    (values: string[]) => {
      setCarrierIdState(values);
      updateArrayParam(URL_KEYS.carrierId, values);
    },
    [updateArrayParam]
  );

  const setLoadOption = useCallback(
    (values: string[]) => {
      setLoadOptionState(values);
      updateArrayParam(URL_KEYS.loadOption, values);
    },
    [updateArrayParam]
  );

  const setSalesRep = useCallback(
    (values: string[]) => {
      setSalesRepState(values);
      updateArrayParam(URL_KEYS.salesRep, values);
    },
    [updateArrayParam]
  );

  const setTag = useCallback(
    (values: string[]) => {
      setTagState(values);
      updateArrayParam(URL_KEYS.tag, values);
    },
    [updateArrayParam]
  );

  const setOrderDateFrom = useCallback(
    (value: string) => {
      setOrderDateFromState(value);
      updateStringParam(URL_KEYS.orderDateFrom, value);
    },
    [updateStringParam]
  );

  const setOrderDateTo = useCallback(
    (value: string) => {
      setOrderDateToState(value);
      updateStringParam(URL_KEYS.orderDateTo, value);
    },
    [updateStringParam]
  );

  const setPickupDateFrom = useCallback(
    (value: string) => {
      setPickupDateFromState(value);
      updateStringParam(URL_KEYS.pickupDateFrom, value);
    },
    [updateStringParam]
  );

  const setPickupDateTo = useCallback(
    (value: string) => {
      setPickupDateToState(value);
      updateStringParam(URL_KEYS.pickupDateTo, value);
    },
    [updateStringParam]
  );

  const setDeliveryDateFrom = useCallback(
    (value: string) => {
      setDeliveryDateFromState(value);
      updateStringParam(URL_KEYS.deliveryDateFrom, value);
    },
    [updateStringParam]
  );

  const setDeliveryDateTo = useCallback(
    (value: string) => {
      setDeliveryDateToState(value);
      updateStringParam(URL_KEYS.deliveryDateTo, value);
    },
    [updateStringParam]
  );

  /*
   * Optional batched date-range setters.
   *
   * Your existing individual setters work because searchParamsRef
   * prevents one update from overwriting the other. These range
   * functions are still useful for cleaner TableFilterBar definitions.
   */
  const setOrderDateRange = useCallback(
    ({ from, to }: { from: string; to: string }) => {
      setOrderDateFromState(from);
      setOrderDateToState(to);

      updateUrlParams((params) =>
        applyDateRange(params, URL_KEYS.orderDateFrom, URL_KEYS.orderDateTo, from, to)
      );
    },
    [updateUrlParams]
  );

  const setPickupDateRange = useCallback(
    ({ from, to }: { from: string; to: string }) => {
      setPickupDateFromState(from);
      setPickupDateToState(to);

      updateUrlParams((params) =>
        applyDateRange(params, URL_KEYS.pickupDateFrom, URL_KEYS.pickupDateTo, from, to)
      );
    },
    [updateUrlParams]
  );

  const setDeliveryDateRange = useCallback(
    ({ from, to }: { from: string; to: string }) => {
      setDeliveryDateFromState(from);
      setDeliveryDateToState(to);

      updateUrlParams((params) =>
        applyDateRange(params, URL_KEYS.deliveryDateFrom, URL_KEYS.deliveryDateTo, from, to)
      );
    },
    [updateUrlParams]
  );

  /*
   * A sales rep opens the page on their own loads the first time, as the old
   * Assignment dropdown did by defaulting them to "Mine". The filter itself
   * lives in the URL now, so only the fact that the guess has run is stored —
   * otherwise clearing to All would be re-seeded on every reload.
   */
  useEffect(() => {
    // Nothing to judge from yet — marking it seeded here would skip the guess.
    if (!me?.firebaseUid) return;

    // Per user, so another login on the same browser doesn't use up the guess.
    const seedKey = `${SALES_REP_SEED_KEY}:${me.firebaseUid}`;

    const alreadySeeded = window.localStorage.getItem(seedKey) === 'true';

    if (alreadySeeded) return;

    const seed = resolveSalesRepSeed(me, salesRepRef.current, false);

    // Only mark it seeded once it applied, so someone who isn't a rep yet (or
    // arrived via a URL/saved filter) still gets it after they're assigned.
    if (seed) {
      window.localStorage.setItem(seedKey, 'true');

      setSalesRep(seed);
    }
  }, [me, setSalesRep]);

  const { sortBy, sortDirection, handleSort, clearSort } = useSortableColumns({
    defaultSortBy: 'date',
    defaultDirection: 'desc',
  });

  const debouncedSearch = useDebouncedSearch(search, 350);

  const { data, isLoading, isFetching } = useListOrgOrderItems({
    ...buildListParams({
      search: debouncedSearch,
      status,
      productId,
      locationId,
      deliveryState,
      pickupState,
      pickupCity,
      truckNumber,
      driverName,
      carrierId,
      loadOption,
      orderDateFrom,
      orderDateTo,
      pickupDateFrom,
      pickupDateTo,
      deliveryDateFrom,
      deliveryDateTo,
      page,
      pageSize,
      sortBy,
      sortDirection,
      salesRep,
      tag,
    }),
    ...(qboInvoice ? { qboInvoice } : {}),
  });

  const approveItem = useApproveOrgItem();
  const rejectItem = useRejectOrgItem();
  const updateItem = useUpdateOrgItem();

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const total = data?.total ?? 0;

  const totalPages = computeTotalPages(total, pageSize);

  /*
   * Flatten rows so one order item equals one table row.
   */
  const rows = useMemo(() => flattenOrderRows(items), [items]);

  /*
   * Row selection can survive pagination, but it is cleared when
   * filters change because those selected records may no longer
   * belong to the current result set.
   */
  const selection = useRowSelection<any>(rows);

  const clearSelection = selection.clear;

  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [
    qboInvoice,
    clearSelection,
    status,
    debouncedSearch,
    productId,
    locationId,
    pageSize,
    deliveryState,
    pickupState,
    pickupCity,
    truckNumber,
    driverName,
    carrierId,
    loadOption,
    salesRep,
    orderDateFrom,
    orderDateTo,
    pickupDateFrom,
    pickupDateTo,
    deliveryDateFrom,
    deliveryDateTo,
    tag,
  ]);

  /*
   * Modals
   */
  const manageModal = useModal<any>();
  const notesModal = useModal<any>();

  const openManage = (row: any) => {
    manageModal.open(row);
  };

  const closeManage = () => {
    manageModal.close();
  };

  const openNotes = (row: any) => {
    notesModal.open(row);
  };

  const closeNotes = () => {
    notesModal.close();
  };

  /**
   * Returns whether the save actually landed.
   *
   * The caller needs to know: the dialog re-baselines its unsaved-changes
   * comparison on success, and must not do that when the request failed. This
   * used to swallow the outcome entirely, so the only signal was a toast.
   */
  const saveItem = async (payload: any, isApprovedAlready: boolean): Promise<boolean> => {
    const { pendingCarrierId, ...rest } = payload;

    try {
      if (pendingCarrierId === null) {
        await removeCarrierDirect(payload.itemId);
      } else if (pendingCarrierId !== undefined) {
        await assignCarrierDirect(payload.itemId, pendingCarrierId);
      }

      if (isApprovedAlready) {
        await updateItem.mutateAsync(rest);

        toast.success('Item updated');
      } else {
        await approveItem.mutateAsync(rest);

        toast.success('Item approved');
      }

      return true;
    } catch (error: any) {
      toast.error(describeOrderActionError(error?.response?.data?.error, 'Save failed'));

      return false;
    }
  };

  const rejectWithReason = async (payload: {
    orderId: string;
    itemId: string;
    reason?: string;
  }) => {
    try {
      await rejectItem.mutateAsync({
        ...payload,
        reason: payload.reason?.trim() || undefined,
      });

      toast.success('Item rejected');
      closeManage();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Reject failed');
    }
  };

  /*
   * The modal used to be re-opened here with the newest row from every refetch,
   * which is how it stayed current. That is gone: the dialog reads the load by
   * id through `useOrgOrderItem`, so it no longer depends on the row surviving
   * in this list at all.
   *
   * Removing it fixes rather than loses behaviour. The effect gave up silently
   * when the row was absent (`if (!freshRow) return`), and any save that changed
   * something the table filters or sorts on made it absent — so the dialog
   * stopped being updated exactly when it most needed to be. It also rebuilt the
   * form from a new row object on every refetch, including the automatic one on
   * window focus, re-running each reconciliation effect mid-edit.
   *
   * `manageModal.data` is still the row the dialog was opened from. It supplies
   * the id, the prev/next position in the table, and the placeholder the dialog
   * paints on its first frame — never its live data.
   */

  return {
    /*
     * Data
     */
    items,
    rows,
    total,
    totalPages,
    isLoading,
    isFetching,

    /*
     * Row selection
     */
    selection,

    /*
     * Filters and paging
     */
    qboInvoice,
    status,
    search,
    productId,
    locationId,

    deliveryState,
    pickupState,
    pickupCity,
    truckNumber,
    driverName,
    carrierId,
    loadOption,
    tag,

    orderDateFrom,
    orderDateTo,
    pickupDateFrom,
    pickupDateTo,
    deliveryDateFrom,
    deliveryDateTo,

    page,
    pageSize,
    setQboInvoice,
    setStatus,
    setSearch,
    setProductId,
    setLocationId,

    setDeliveryState,
    setPickupState,
    setPickupCity,
    setTruckNumber,
    setDriverName,
    setCarrierId,
    setLoadOption,
    setTag,

    setOrderDateFrom,
    setOrderDateTo,
    setPickupDateFrom,
    setPickupDateTo,
    setDeliveryDateFrom,
    setDeliveryDateTo,

    /*
     * Batched date-range setters.
     */
    setOrderDateRange,
    setPickupDateRange,
    setDeliveryDateRange,

    setPage,
    setPageSize,

    sortBy,
    sortDirection,
    handleSort,
    clearSort,

    /*
     * Sales reps
     */
    salesRep,
    setSalesRep,

    /*
     * Modals
     */
    manageModal,
    notesModal,
    openManage,
    closeManage,
    openNotes,
    closeNotes,

    /*
     * Mutations and actions
     */
    approveItem,
    rejectItem,
    updateItem,
    saveItem,
    rejectWithReason,
  };
}
